import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { formatDistanceToNow } from 'date-fns';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLocalization } from '@/shared/hooks/useLocalization';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsList,
  useUnreadNotificationsCount
} from '@/shared/hooks/useNotifications';
import type { NotificationRecord } from '@/shared/types/notification.types';
import { resolveNotificationRoute } from '@/shared/utils/notification-deeplink';
import { deriveNotificationDisplay } from '@/shared/utils/notification-display';

const iconFor = (type: string): React.ReactElement => {
  const t = (type || '').toLowerCase();
  if (t.includes('whatsapp') || t.includes('message') || t.includes('comment'))
    return <ChatBubbleOutlineIcon fontSize="small" color="secondary" />;
  if (t.includes('assign')) return <PersonAddOutlinedIcon fontSize="small" color="info" />;
  if (t.includes('status') || t.includes('update'))
    return <UpdateOutlinedIcon fontSize="small" color="warning" />;
  if (t.includes('resolve') || t.includes('complete'))
    return <CheckCircleOutlineIcon fontSize="small" color="success" />;
  if (t.includes('ticket') || t.includes('helpdesk'))
    return <ConfirmationNumberOutlinedIcon fontSize="small" color="primary" />;
  return <NotificationsNoneIcon fontSize="small" />;
};

export const NotificationBell: React.FC = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { data: unreadCount = 0 } = useUnreadNotificationsCount();
  const { data, isLoading, isError } = useNotificationsList({ limit: 20 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications: NotificationRecord[] = data?.items ?? [];

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleItemClick = (notification: NotificationRecord): void => {
    // Guard against id being missing: the previous wire-shape mismatch
    // (`_id` vs `id`) caused `PATCH /notify/undefined/read` to 404 the
    // entire interaction. Skipping the mark-read call when id is falsy
    // means a notification with a broken shape still navigates instead
    // of silently failing.
    if (!notification.isRead && notification.id) markRead.mutate(notification.id);
    const path = resolveNotificationRoute(notification);
    if (path) navigate(path);
    handleClose();
  };

  const handleMarkAllRead = (): void => {
    markAllRead.mutate();
  };

  return (
    <>
      <IconButton aria-label="notifications" onClick={handleOpen}>
        <Badge badgeContent={unreadCount} color="error" max={99} invisible={unreadCount === 0}>
          {unreadCount > 0 ? (
            <NotificationsActiveIcon color="primary" />
          ) : (
            <NotificationsNoneIcon color="primary" />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 460 } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant="h6">{t('SHARED.NOTIFICATION_BELL.TITLE')}</Typography>
          <Button
            size="small"
            disabled={unreadCount === 0 || markAllRead.isPending}
            onClick={handleMarkAllRead}
          >
            {t('SHARED.NOTIFICATION_BELL.MARK_ALL_READ')}
          </Button>
        </Stack>

        <Divider />

        {isLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 4, gap: 1 }}
          >
            <NotificationsOffIcon color="disabled" fontSize="large" />
            <Typography variant="body2" color="text.secondary">
              {t('SHARED.NOTIFICATION_BELL.ERROR_LOADING')}
            </Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 4, gap: 1 }}
          >
            <NotificationsOffIcon color="disabled" fontSize="large" />
            <Typography variant="body2" color="text.secondary">
              {t('SHARED.NOTIFICATION_BELL.NO_NOTIFICATIONS')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflowY: 'auto', maxHeight: 380 }}>
            {notifications.map((notification) => {
              // Notification records vary by type — push has title/body in
              // pushNotificationResponse; whatsapp template-sends have
              // templateId + phoneNumber. Centralised in
              // deriveNotificationDisplay so the bell + future surfaces stay
              // consistent.
              const display = deriveNotificationDisplay(notification);
              const primary = display.title;
              const secondary = [
                display.body,
                formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <React.Fragment key={notification.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleItemClick(notification)}
                      sx={{ bgcolor: notification.isRead ? 'transparent' : 'action.hover' }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {iconFor(notification.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={primary}
                        secondary={secondary}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: notification.isRead ? 400 : 600,
                          noWrap: false
                        }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
